// VerificadorDocumento — selector de archivo + verificación (OCR y cruce
// de datos) con el servicio de Ecolab/Fenagro (ver services/
// ecolabDocumentos.js — uso TRANSITORIO).
//
// Portado del componente del portal de Fenagro, más simple en lo visual
// pero con las mismas reglas:
//   • Solo PDF / JPG / PNG, entre 5 KB y 10 MB.
//   • Si el servicio de verificación falla o rechaza el documento, NO se
//     acepta (en Fenagro se cerró a propósito el "dejar pasar para
//     revisión manual": permitía subir cualquier archivo con el
//     servicio caído).
//   • Solo se acepta si además el servicio devuelve la ubicación
//     guardada (urlDocumento).
//
// Para reiniciarlo desde afuera (p. ej. cuando cambian los datos con los
// que se cruzó), el padre le cambia la `key`.

import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { verificarDocumentoEcolab } from '../services/ecolabDocumentos';

const EXTENSIONES = ['jpg', 'jpeg', 'png', 'pdf'];
const PASOS_UI = [
  'Leyendo QR y código de barras…',
  'OCR: extrayendo texto del documento…',
  'Cruzando datos con el formulario…',
  'Validando resultado…',
];

/**
 * @param {string} [titulo]
 * @param {string} [descripcion]
 * @param {'rut'|'cedula'} tipoDocumento
 * @param {string} numero   NIT (rut) o número de cédula
 * @param {string} nombres  razón social (rut) o nombre del titular (cédula)
 * @param {(file: File, resultado: object) => void} onValido
 * @param {() => void} onInvalido
 */
export default function VerificadorDocumento({
  titulo,
  descripcion,
  tipoDocumento = 'rut',
  numero = '',
  nombres = '',
  apellidos = '',
  onValido,
  onInvalido,
}) {
  const inputRef = useRef(null);
  const [estado, setEstado] = useState('idle'); // idle | verificando | valido | invalido
  const [archivo, setArchivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const [paso, setPaso] = useState(0);
  const [segundos, setSegundos] = useState(0);

  // Mientras espera al servidor, rota el texto del paso y cuenta segundos.
  useEffect(() => {
    if (estado !== 'verificando') return undefined;
    const t = setInterval(() => {
      setSegundos((s) => s + 1);
      setPaso((p) => Math.min(p + 1, PASOS_UI.length - 1));
    }, 1500);
    return () => clearInterval(t);
  }, [estado]);

  const limpiar = () => {
    setEstado('idle');
    setArchivo(null);
    setResultado(null);
    setError('');
    setPaso(0);
    setSegundos(0);
    if (inputRef.current) inputRef.current.value = '';
    onInvalido?.();
  };

  const rechazar = (mensaje) => {
    setEstado('invalido');
    setError(mensaje);
    setResultado(null);
    onInvalido?.();
  };

  const procesar = async (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!EXTENSIONES.includes(ext)) return rechazar(`Formato ".${ext}" no permitido. Use JPG, PNG o PDF.`);
    if (f.size > 10 * 1024 * 1024) return rechazar('El archivo supera los 10 MB permitidos.');
    if (f.size < 5 * 1024) return rechazar('El archivo es demasiado pequeño. Puede estar en blanco o dañado.');

    setArchivo(f);
    setError('');
    setPaso(0);
    setSegundos(0);
    setEstado('verificando');

    try {
      const res = await verificarDocumentoEcolab(f, { tipoDocumento, numero, nombres, apellidos });
      if (!res.valido) return rechazar(res.mensaje || 'Verificación incompleta: se requiere revisión manual.');
      if (!res.urlDocumento) {
        return rechazar(
          'El documento se verificó, pero el servicio de almacenamiento no devolvió su ubicación. ' +
            'Intenta de nuevo o avisa al administrador.'
        );
      }
      setResultado(res);
      setEstado('valido');
      onValido?.(f, res);
      return undefined;
    } catch (e) {
      // Si el servicio falla, NO se acepta el documento (ver encabezado).
      return rechazar(e.message || 'No se pudo verificar el documento. Intenta de nuevo más tarde.');
    }
  };

  return (
    <Box>
      {titulo && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
      )}
      {descripcion && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {descripcion}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(e) => procesar(e.target.files?.[0])}
      />

      {estado === 'idle' && (
        <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => inputRef.current?.click()}>
          Seleccionar archivo
        </Button>
      )}

      {estado === 'verificando' && (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {archivo?.name} — {PASOS_UI[paso]} ({segundos}s)
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {estado === 'valido' && (
        <Alert
          severity="success"
          action={
            <Button color="inherit" size="small" onClick={limpiar}>
              Cambiar
            </Button>
          }
        >
          <b>{archivo?.name}</b> verificado
          {resultado?.confianza != null ? ` (${resultado.confianza}% de confianza)` : ''}.
          {resultado?.mensaje ? ` ${resultado.mensaje}` : ''}
        </Alert>
      )}

      {estado === 'invalido' && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={limpiar}>
              Intentar de nuevo
            </Button>
          }
        >
          {error}
        </Alert>
      )}
    </Box>
  );
}
